import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Mail, Send, Eye, Gift, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import AppHeader from '../components/AppHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAYS = [
  { value: 'sunday', label: 'Sunday' },
  { value: 'monday', label: 'Monday' },
  { value: 'tuesday', label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday', label: 'Thursday' },
  { value: 'friday', label: 'Friday' },
  { value: 'saturday', label: 'Saturday' }
];

export default function Settings() {
  const [prefs, setPrefs] = useState({ enabled: true, frequency: 'weekly', day_of_week: 'sunday' });
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [referral, setReferral] = useState(null);
  const [copied, setCopied] = useState(false);

  const fetchPrefs = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/digest/preferences`, { withCredentials: true });
      setPrefs(data);
    } catch (error) {
      console.error('Error fetching preferences:', error);
    }
  }, []);

  const fetchReferral = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/referrals/status`, { withCredentials: true });
      setReferral(data);
    } catch (error) {
      console.error('Error fetching referral:', error);
    }
  }, []);

  useEffect(() => {
    fetchPrefs();
    fetchReferral();
  }, [fetchPrefs, fetchReferral]);

  const copyReferralLink = () => {
    if (!referral?.share_url) return;
    navigator.clipboard.writeText(referral.share_url);
    setCopied(true);
    toast.success('Referral link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const shareViaText = () => {
    const message = `Hey! I've been using Stable Stationary to write letters to a loved one who's inside. Sign up with my link and we both get an extra week free: ${referral.share_url}`;
    if (navigator.share) {
      navigator.share({ title: 'Stable Stationary', text: message, url: referral.share_url });
    } else {
      window.location.href = `sms:?&body=${encodeURIComponent(message)}`;
    }
  };

  const savePreferences = async (updated) => {
    setSaving(true);
    try {
      await axios.put(`${API}/digest/preferences`, updated, { withCredentials: true });
      setPrefs(updated);
      toast.success('Preferences saved');
    } catch (error) {
      toast.error('Failed to save');
    }
    setSaving(false);
  };

  const handleSendNow = async () => {
    setSending(true);
    try {
      await axios.post(`${API}/digest/send-now`, {}, { withCredentials: true });
      toast.success('Digest sent to your email!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send digest');
    }
    setSending(false);
  };

  const handlePreview = async () => {
    setLoadingPreview(true);
    try {
      const { data } = await axios.post(`${API}/digest/preview`, {}, { withCredentials: true });
      setPreview(data);
    } catch (error) {
      toast.error('Failed to load preview');
    }
    setLoadingPreview(false);
  };

  return (
    <div className="min-h-screen bg-[#F9F7F3]">
      <AppHeader />
      <div className="max-w-4xl mx-auto px-4 md:px-6 py-8 md:py-12">
        <Link to="/dashboard">
          <Button variant="ghost" size="sm" className="mb-4" data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-2" />Back to Dashboard
          </Button>
        </Link>
        <h1 className="text-4xl font-light tracking-tight mb-2" style={{fontFamily: 'Spectral, serif'}}>Settings</h1>
        <p className="text-base text-[#737373] mb-8">Manage your weekly digest and email preferences.</p>

        {/* Referrals card */}
        {referral && (
          <div className="bg-white border border-[#E5E0D8] rounded-md p-8 shadow-[2px_2px_0px_rgba(26,26,26,0.08)] mb-6" data-testid="referrals-card">
            <div className="flex items-start gap-4 mb-6">
              <Gift className="w-8 h-8 text-[#B94A3E]" strokeWidth={1.5} />
              <div className="flex-1">
                <h2 className="text-2xl mb-1" style={{fontFamily: 'Spectral, serif'}}>Refer a Friend, Extend Your Trial</h2>
                <p className="text-sm text-[#737373]">Share Stable Stationary with someone who has a loved one inside. When they sign up, you both get <strong>+7 days</strong> added to your free trials.</p>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="bg-[#F9F7F3] rounded-md p-4 text-center">
                <div className="text-3xl font-light text-[#B94A3E]" style={{fontFamily: 'Spectral, serif'}} data-testid="successful-referrals-count">{referral.successful_referrals}</div>
                <div className="text-xs label-text mt-1">Friends Joined</div>
              </div>
              <div className="bg-[#F9F7F3] rounded-md p-4 text-center">
                <div className="text-3xl font-light text-[#B94A3E]" style={{fontFamily: 'Spectral, serif'}} data-testid="bonus-days-earned">{referral.bonus_days_earned}</div>
                <div className="text-xs label-text mt-1">Bonus Days Earned</div>
              </div>
            </div>

            <div className="space-y-3">
              <Label className="label-text">Your Referral Link</Label>
              <div className="flex gap-2">
                <div className="flex-1 bg-[#F9F7F3] border border-[#E5E0D8] rounded-md px-4 py-2.5 font-mono text-sm truncate" data-testid="referral-link">
                  {referral.share_url}
                </div>
                <Button
                  onClick={copyReferralLink}
                  variant="outline"
                  className="border-[#E5E0D8] gap-2"
                  data-testid="copy-referral-btn"
                >
                  {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                  {copied ? 'Copied!' : 'Copy'}
                </Button>
                <Button
                  onClick={shareViaText}
                  className="bg-[#B94A3E] hover:bg-[#9d3e33] text-white gap-2"
                  data-testid="share-referral-btn"
                >
                  <Send className="w-4 h-4" />
                  Share
                </Button>
              </div>
              <p className="text-xs text-[#737373]">Your code: <span className="font-mono">{referral.referral_code}</span></p>
            </div>
          </div>
        )}

        <div className="bg-white border border-[#E5E0D8] rounded-md p-8 shadow-[2px_2px_0px_rgba(26,26,26,0.08)] mb-6">
          <div className="flex items-start gap-4 mb-6">
            <Mail className="w-8 h-8 text-[#B94A3E]" strokeWidth={1.5} />
            <div>
              <h2 className="text-2xl mb-1" style={{fontFamily: 'Spectral, serif'}}>Weekly Digest</h2>
              <p className="text-sm text-[#737373]">A summary of your writing progress and upcoming occasions, sent to your inbox.</p>
            </div>
          </div>

          <div className="space-y-6">
            <div className="flex items-center justify-between py-4 border-b border-[#E5E0D8]">
              <div>
                <Label className="font-medium text-base">Enable weekly digest</Label>
                <p className="text-sm text-[#737373] mt-1">Receive a summary of your letters and streaks</p>
              </div>
              <Switch
                checked={prefs.enabled}
                onCheckedChange={(v) => savePreferences({ ...prefs, enabled: v })}
                disabled={saving}
                data-testid="digest-enabled-switch"
              />
            </div>

            <div className="flex items-center justify-between py-4 border-b border-[#E5E0D8]">
              <div>
                <Label className="font-medium text-base">Frequency</Label>
                <p className="text-sm text-[#737373] mt-1">How often would you like to hear from us</p>
              </div>
              <Select
                value={prefs.frequency}
                onValueChange={(v) => savePreferences({ ...prefs, frequency: v })}
                disabled={!prefs.enabled || saving}
              >
                <SelectTrigger className="w-40 border-[#E5E0D8]" data-testid="frequency-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="biweekly">Bi-weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between py-4 border-b border-[#E5E0D8]">
              <div>
                <Label className="font-medium text-base">Day of week</Label>
                <p className="text-sm text-[#737373] mt-1">When should we send your digest</p>
              </div>
              <Select
                value={prefs.day_of_week}
                onValueChange={(v) => savePreferences({ ...prefs, day_of_week: v })}
                disabled={!prefs.enabled || saving}
              >
                <SelectTrigger className="w-40 border-[#E5E0D8]" data-testid="day-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DAYS.map(d => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-3 pt-4">
              <Button onClick={handlePreview} variant="outline" className="border-[#E5E0D8]" disabled={loadingPreview} data-testid="preview-digest-btn">
                <Eye className="w-4 h-4 mr-2" />{loadingPreview ? 'Loading...' : 'Preview Digest'}
              </Button>
              <Button onClick={handleSendNow} className="bg-[#B94A3E] hover:bg-[#9d3e33] text-white" disabled={sending || !prefs.enabled} data-testid="send-digest-btn">
                <Send className="w-4 h-4 mr-2" />{sending ? 'Sending...' : 'Send Now'}
              </Button>
            </div>
          </div>
        </div>

        {preview && (
          <div className="bg-white border border-[#E5E0D8] rounded-md p-8 shadow-[2px_2px_0px_rgba(26,26,26,0.08)]" data-testid="digest-preview">
            <h3 className="text-xl mb-4" style={{fontFamily: 'Spectral, serif'}}>Preview: What you'll receive</h3>
            <div className="bg-[#F9F7F3] rounded-md p-6 space-y-4">
              <p className="text-sm text-[#737373]">Hi {preview.user_name?.split(' ')[0]},</p>
              <p className="text-lg" style={{fontFamily: 'Spectral, serif'}}>
                {preview.letters_written === 0
                  ? `You didn't write to ${preview.recipient_name} this week. It's a good time to reach out.`
                  : preview.letters_written === 1
                  ? `You showed up 1 time this week for ${preview.recipient_name}. Every letter matters.`
                  : `You showed up ${preview.letters_written} times this week for ${preview.recipient_name}. That's what love looks like.`
                }
              </p>
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-white rounded p-4 text-center">
                  <div className="text-2xl text-[#B94A3E]" style={{fontFamily: 'Spectral, serif'}}>{preview.letters_written}</div>
                  <div className="text-xs label-text">Letters</div>
                </div>
                <div className="bg-white rounded p-4 text-center">
                  <div className="text-2xl text-[#B94A3E]" style={{fontFamily: 'Spectral, serif'}}>{preview.letters_mailed}</div>
                  <div className="text-xs label-text">Mailed</div>
                </div>
                <div className="bg-white rounded p-4 text-center">
                  <div className="text-2xl text-[#B94A3E]" style={{fontFamily: 'Spectral, serif'}}>{preview.photos_created}</div>
                  <div className="text-xs label-text">Photos</div>
                </div>
              </div>
              {preview.streak > 0 && (
                <div className="bg-[#B94A3E] text-white rounded p-4 text-center">
                  <div className="text-xs uppercase tracking-widest opacity-80">Current Streak</div>
                  <div className="text-3xl mt-1" style={{fontFamily: 'Spectral, serif'}}>{preview.streak} {preview.streak === 1 ? 'day' : 'days'}</div>
                </div>
              )}
              {preview.upcoming_occasions?.length > 0 && (
                <div className="bg-[#F5EBE0] rounded p-4">
                  <div className="text-sm font-medium mb-2">Upcoming Occasions</div>
                  <ul className="text-sm space-y-1">
                    {preview.upcoming_occasions.map((o) => (
                      <li key={o.name}>{o.name} is in <strong>{o.days_until} days</strong></li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="border-l-4 border-[#B94A3E] pl-4 py-2">
                <div className="text-xs label-text">This Week's Tip</div>
                <p className="text-sm italic mt-1">{preview.writing_tip}</p>
              </div>
              <p className="text-center text-sm text-[#737373] italic">"{preview.quote}"</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
